import React, { useEffect, useState } from 'react'
import useUsersApi from '../../hooks/users/useUsersApi'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import useApartmentsApi from '../../hooks/apartments/useApartmentsApi'
import { userTypes } from '../../utils/constants'
import DeleteConfirmationModal from '../common/DeleteConfirmationModal'
import { useIsSecurityUser } from '../../hooks/useIsSecurityUser'
import CustomTable from '../customTable/CustomTable'
import ShowImageTable from '../common/ShowImageTable'
import { useHistory } from 'react-router-dom'
import ListHeader from '../common/ListHeader'
import useAuthStore from '../../stores/Auth'

export default function UserList({
  building,
  redirectTo,
  withTitle = true,
  pathOverride,
  showOnlyUsers = false,
  isSecurityUser,
  initialType = userTypes.user,
}) {
  const history = useHistory()
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null)
  // const isSecurityUser = useIsSecurityUser()
  const [{ usersState }, { getUsers, deleteUser }] = useUsersApi()
  const [, setSelectedUser] = useState({})

  const loggedUser = useAuthStore((state) => state.user)
  const isAdmin = loggedUser?.type === userTypes.admin

  const options = (user) => [
    {
      key: 'edit',
      label: 'Editar',
      onClick: () => {
        history.push('/nuevo-usuario', { user, previousRoute: redirectTo })
      },
    },
    {
      key: 'delete',
      label: 'Eliminar',
      onClick: () => {
        setSelectedUserToDelete(user)
      },
    },
  ]

  let columns = React.useMemo(
    () =>
      [
        {
          id: 'image',
          label: 'Usuario',
          minWidth: 250,
          order: 0,
          format: (value) => (
            <ShowImageTable
              imageUrl={value.selfie}
              name={`${value.name} ${value.surname}`}
              ci={value.ci || value.email}
            ></ShowImageTable>
          ),
        },
        {
          id: 'email',
          label: 'Email',
          minWidth: 100,
          order: 4,
        },
        initialType !== userTypes.security && {
          id: 'building',
          label: isSecurityUser ? 'Unidad' : 'Cliente/Unidad',
          minWidth: 120,
          order: 3,
          format: (value) => getBuildingApartmentLabel(value),
        },
        {
          id: 'phone',
          label: 'Telefono',
          minWidth: 100,
          order: 5,
        },
        !isSecurityUser
          ? {
              id: 'enabled',
              label: 'Habilitado',
              minWidth: 100,
              order: 6,
            }
          : null,
        !isSecurityUser
          ? {
              id: 'doorbellNotificationsEnabled',
              label: 'Notificaciones porteros',
              minWidth: 100,
              order: 6,
            }
          : null,
        !isSecurityUser
          ? {
              id: 'openDoorNotificationsEnabled',
              label: 'Notificaciones autorizados',
              minWidth: 100,
              order: 6,
            }
          : null,
        !isSecurityUser && {
          id: 'deviceModel',
          label: 'Dispositivo',
          minWidth: 100,
          order: 7,
        },
        !isSecurityUser
          ? {
              id: 'appVersion',
              label: 'Version app',
              minWidth: 100,
              order: 8,
            }
          : null,
        !isSecurityUser
          ? {
              id: 'osVersion',
              label: 'Version SO',
              minWidth: 100,
              order: 9,
            }
          : null,
        !isSecurityUser ? { id: '', label: '', minWidth: 5, order: 10 } : null,
      ].filter((c) => !!c),
    [isSecurityUser]
  )

  const [serverPagination, setServerPagination] = useState(true)
  const [{ buildingsState }, { getBuildings }] = useBuildingsApi()
  const [
    { getApartmentsFromBuildingState, apartmentsState },
    { getApartmentsFromBuilding, getApartments },
  ] = useApartmentsApi()

  useEffect(() => {
    const fetchData = async () => {
      getBuildings()
      getApartments()
    }
    fetchData()
  }, [])

  const getBuildingApartmentLabel = (row) => {
    if (row?.apartments && row.apartments.length > 0) {
      if (isSecurityUser) {
        return row.apartments.map((a) => a.name).join(', ')
      }
      return row.apartments
        .map((a) => `${a.building?.name || ''} - ${a.name}`)
        .join(', ')
    }

    const apartment = apartmentsState.apartments.find(
      (apt) => apt.id === row.apartmentId
    )
    const building =
      row.building ||
      buildingsState.buildings.find(
        (build) => build.id === apartment?.buildingId
      )
    if (isSecurityUser) {
      return apartment?.name
    } else {
      return apartment
        ? `${building?.name} - ${apartment?.name}`
        : building?.name
    }
  }

  columns = columns.sort((c1, c2) => (c1.order > c2.order ? 1 : -1))
  const users = usersState?.data?.users || []

  if (isSecurityUser && !building) {
    return <></>
  }

  const getBigTitle = () => {
    if (!withTitle) return 'Residentes'
    if (isSecurityUser && initialType === userTypes.security) return 'Seguridad'
    if (isAdmin) return 'Usuarios'
    return 'Residentes'
  }
  return (
    <>
      <ListHeader
        bigTitle={getBigTitle()}
        createText={isAdmin ? 'Crear usuario' : 'Crear residente'}
        canCreate={!isSecurityUser}
        createRedirect={() => {
          setSelectedUser({})
          history.push('/nuevo-usuario', {
            previousRoute: redirectTo,
            pathOverride,
          })
        }}
      />
      <CustomTable
        loading={usersState.isLoading}
        columns={columns}
        data={users || []}
        getData={(params) => {
          getUsers({
            type: initialType || userTypes.user,
            ...params,
            showSelfie: true,
          })
        }}
        buildingFilter={!building}
        building={building}
        apartmentFilter={!isSecurityUser || initialType !== userTypes.security}
        searchTitle={'Nombre, apellido o documento de identidad'}
        searchPlaceholder={
          'Ingrese un nombre, apellido o documento de identidad'
        }
        options={!isSecurityUser && options}
        serverPagination={serverPagination}
        serverCount={usersState?.data?.count}
        handleFiltersChange={(params) => {
          if (params.type === userTypes.unassignedUser) {
            setServerPagination(false)
          }
        }}
        customFilters={
          !showOnlyUsers
            ? [
                {
                  field: 'type',
                  label: 'Tipo de usuario',
                  type: 'select',
                  defaultValue: isSecurityUser
                    ? userTypes.security
                    : userTypes.user,
                  options: isSecurityUser
                    ? [
                        { value: userTypes.security, label: 'Seguridad' },
                        {
                          value: userTypes.securityAdmin,
                          label: 'Seguridad Administrador',
                        },
                      ]
                    : [
                        { value: userTypes.admin, label: 'Administrador' },
                        { value: userTypes.operator, label: 'Operador' },
                        { value: userTypes.user, label: 'Usuario' },
                        {
                          value: userTypes.buildingAdmin,
                          label: 'Administrador de edificio',
                        },
                        { value: userTypes.security, label: 'Seguridad' },
                        {
                          value: userTypes.securityAdmin,
                          label: 'Seguridad Administrador',
                        },
                        { value: userTypes.gestor, label: 'Gestor' },
                        {
                          value: userTypes.unassignedUser,
                          label: 'Usuario sin asignar',
                        },
                      ],
                },
              ]
            : []
        }
      ></CustomTable>
      <DeleteConfirmationModal
        open={!!selectedUserToDelete}
        invitation={selectedUserToDelete}
        onClose={() => setSelectedUserToDelete(null)}
        onDelete={(user) => {
          deleteUser(user.id)
          setSelectedUserToDelete(null)
        }}
        text="¿Está seguro que desea eliminar este usuario?"
      />
    </>
  )
}
